import { type Column } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import type { ReportPeriod } from '../report-period.ts';
import { usePageContext } from '../usePageContext.ts';
import { QueryView } from '../QueryView.tsx';
import { deliveryRateColumn, numericColumn } from './columns.ts';
import { StatisticsTable } from './StatisticsTable.tsx';
import { rateOf, useStatisticsFormat, useStatisticsReport, type MarketingReport } from './statistics.ts';
import styles from './Statistics.module.css';

type ChannelRow = MarketingReport['channels'][number];

const MarketingFigures = ({ report }: { report: MarketingReport }) => {
  const { t } = usePageContext();
  const format = useStatisticsFormat(report.currency);
  const columns: readonly Column<ChannelRow>[] = [
    {
      id: 'channel',
      header: t('statistics.channel'),
      primary: true,
      cell: (row) => t(`orders.source_${row.source ?? 'none'}`),
    },
    numericColumn('orders', t('statistics.orders'), (row) => format.count(row.orders)),
    numericColumn('confirmation', t('statistics.confirmationRate'), (row) =>
      format.rate(rateOf(row.confirmed, row.handled)),
    ),
    deliveryRateColumn(t('statistics.deliveryRate'), format),
    numericColumn('revenue', t('statistics.revenue'), (row) => format.money(row.revenue)),
  ];
  return (
    <div className={styles['stack']}>
      <p className={styles['notice']}>{t('statistics.campaignsLater')}</p>
      <StatisticsTable
        title={t('statistics.channelsTitle')}
        help={t('statistics.channelsHelp')}
        columns={columns}
        rows={report.channels}
        rowKey={(row) => row.source ?? 'none'}
        emptyTitle={t('statistics.noOrders')}
      />
    </div>
  );
};

export const MarketingStatistics = ({ api, period }: { api: TypedClient; period: ReportPeriod }) => {
  const { t } = usePageContext();
  const query = useStatisticsReport('marketing', period, {}, () =>
    api.get('/admin/statistics/marketing', { query: period }),
  );
  return (
    <QueryView query={query} label={t('statistics.tab_marketing')}>
      {(report) => <MarketingFigures report={report} />}
    </QueryView>
  );
};
